import React, { useEffect, useRef, useState } from 'react';
import type { Me } from './authApi';

interface Props {
  me: Me;
  onAccess: () => void;
  onLogout: () => void;
  onLogoutAll: () => void;
  /**
   * floating = przypięte w prawym górnym rogu ekranu (fallback, np. /new);
   * inline = w pasku nagłówka aplikacji (AccountMenuSlot) — nie nachodzi na nagłówek ani karty.
   */
  variant?: 'floating' | 'inline';
}

const item = 'block min-h-[44px] w-full rounded-lg px-3 text-left text-sm text-txt-primary hover:bg-accent-soft focus-visible:bg-accent-soft';
const initials = (email: string) => (email.split('@')[0].replace(/[^\p{L}\p{N}]/gu, '').slice(0, 2) || '?').toUpperCase();

/** Account menu: inline in the app header when it hosts a slot, otherwise fixed top-right (see AccountMenuContext). */
const UserMenu: React.FC<Props> = ({ me, onAccess, onLogout, onLogoutAll, variant = 'floating' }) => {
  const [open, setOpen] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  const close = (refocus = true) => { setOpen(false); setConfirmAll(false); if (refocus) trigger.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDoc = (e: MouseEvent | TouchEvent) => { if (root.current && !root.current.contains(e.target as Node)) close(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        if (!items.length) return;
        e.preventDefault();
        const i = items.indexOf(document.activeElement as HTMLElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus();
      }
    };
    document.addEventListener('mousedown', onDoc); document.addEventListener('touchstart', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('touchstart', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={root} className={`auth-menu print:hidden ${variant === 'inline' ? 'relative shrink-0' : 'fixed z-menu'}`}
      style={variant === 'inline' ? undefined : { top: 'max(0.5rem, env(safe-area-inset-top))', right: 'max(0.5rem, env(safe-area-inset-right))' }}>
      <button ref={trigger} type="button" aria-label="Menu użytkownika" aria-haspopup="menu" aria-expanded={open}
        onClick={() => { setOpen(o => !o); setConfirmAll(false); }}
        className={`flex items-center justify-center rounded-full text-sm font-semibold ${variant === 'inline' ? 'h-10 w-10 border border-accent-border bg-accent-soft text-accent-strong hover:border-accent-strong' : 'glass-float h-11 w-11 text-txt-primary hover:bg-accent-soft'}`}>
        {initials(me.email)}
      </button>
      {open && (
        <div role="menu" aria-label="Konto" className="glass-menu auth-rise absolute right-0 z-menu mt-2 w-[min(18rem,calc(100vw-1.5rem))] rounded-xl p-2">
          <div className="px-3 py-2">
            <p className="break-all text-sm font-medium text-txt-primary">{me.email}</p>
            <p className="text-xs text-txt-secondary">{me.role === 'admin' ? 'Administrator' : 'Diagnosta'}</p>
          </div>
          <div className="my-1 h-px bg-brd" role="separator" />
          {me.role === 'admin' && <button type="button" role="menuitem" className={item} onClick={() => { close(false); onAccess(); }}>Dostępy</button>}
          <button type="button" role="menuitem" className={item} onClick={onLogout}>Wyloguj</button>
          {!confirmAll ? (
            <button type="button" role="menuitem" className={item} onClick={() => setConfirmAll(true)}>Wyloguj ze wszystkich urządzeń</button>
          ) : (
            <div className="mt-1 rounded-lg bg-surface-subtle p-3" role="group" aria-label="Potwierdzenie">
              <p className="text-sm text-txt-primary">Wylogować ze wszystkich urządzeń? Wszystkie sesje tego konta zostaną zakończone.</p>
              <div className="mt-2 flex gap-2">
                <button type="button" role="menuitem" onClick={onLogoutAll}
                  className="min-h-[44px] flex-1 rounded-lg bg-accent-strong px-3 text-sm font-semibold text-txt-onAccent">Wyloguj wszędzie</button>
                <button type="button" role="menuitem" onClick={() => setConfirmAll(false)}
                  className="min-h-[44px] flex-1 rounded-lg border border-brd bg-surface-card px-3 text-sm text-txt-primary hover:bg-surface-subtle">Anuluj</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMenu;
